/**
 * The mark a MONITORING row carries — and, for healthy, the mark it does not.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT `StatusBadge`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `StatusBadge` is the compliance vocabulary: pass / warn / fail / na, labelled "Compliant",
 * "Attention", "Gap". On the compliance matrix a pass IS an answer an auditor argues from, so
 * it is drawn. On a monitoring screen it is not — healthy is the absence of a state
 * (`docs/DESIGN-PHILOSOPHY.md` §1, §3.3), and painting twelve reporting machines green is how
 * the one red one got drowned. So the two are separate components and `StatusBadge` stays
 * exactly as it is for compliance.
 *
 * `quiet` renders NOTHING — not a pale dot, not a tick. Only fail, warn and unknown earn a
 * mark, and one mark per item.
 *
 * NOT COLOUR ALONE (WCAG 1.4.1): the three marks differ in SHAPE — a filled disc, a filled
 * triangle, a hollow ring — so they still read apart in greyscale or to a colour-blind
 * reader, and each carries its own accessible name.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type HealthTone = 'fail' | 'warn' | 'unknown' | 'quiet';

const NAME: Record<Exclude<HealthTone, 'quiet'>, string> = {
  fail: 'Needs action',
  warn: 'Attention',
  unknown: 'Unknown',
};

export function HealthMark({
  tone,
  size = 10,
  className,
}: {
  tone: HealthTone;
  size?: number;
  className?: string;
}): ReactNode {
  if (tone === 'quiet') return null;
  const common = cn('inline-block shrink-0', className);
  if (tone === 'warn') {
    return (
      <svg role="img" aria-label={NAME.warn} width={size} height={size} viewBox="0 0 10 10" className={common}>
        <path d="M5 0.6 9.6 9.2H0.4Z" fill="var(--warn)" />
      </svg>
    );
  }
  return (
    <span
      role="img"
      aria-label={NAME[tone]}
      className={cn(
        common,
        'rounded-full',
        tone === 'fail' ? 'bg-[var(--danger)]' : 'border-[1.5px] border-[var(--muted-fg)]',
      )}
      /* Size is a prop, so it stays inline; everything static is a class. */
      style={{ width: size, height: size }}
    />
  );
}
